import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ArboristNodeData,
  // transformDiscoveredTopicsToTreeData, // removed: backend now returns tree structure
} from "../../lib/utils";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import {
  DeviceMonitoredTopic,
  CreateDeviceMonitoredTopicRequest,
} from "../../interfaces/MonitoredTopics";

export interface McapRecorderStorage {
  url: string;
  api_token?: string;
  bucket: string;
  quota_type?: string;
  quota_size?: number;
  max_block_size?: number;
  max_block_records?: number;
}

export interface McapRecorderPipeline {
  "split.max_duration_s": number;
  "split.max_size_bytes"?: number | string;
  include_topics: string[];
  exclude_topics?: string[];
  static_labels?: Record<string, string>;
  compression?: string;
  enable_crcs?: boolean;
  spool_max_size_bytes?: number;
  downsampling_mode?: string;
  max_rate_hz?: number;
  stride_n?: number;
}

/** How a device's recording uploads may use its link (agents 1.0.0+). */
export interface McapRecorderUpload {
  /** Give way to other traffic on the link. Unset means on. */
  adaptive?: boolean;
  /** Cap in megabits per second; unset means none. */
  max_rate_mbps?: number;
}

export interface McapRecorderConfig {
  enabled?: boolean;
  storage: McapRecorderStorage;
  pipelines: Record<string, McapRecorderPipeline>;
  upload?: McapRecorderUpload;
}

export interface Device {
  id: string;
  token: string;
  name: string;
  description: string;
  online: boolean;
  lastSeenAt: number;
  type?:
    | "Robot Arm"
    | "AMR"
    | "UGV"
    | "Drone"
    | "Humanoid"
    | "Sensor Hub"
    | "Quadruped"
    | "Autonomous Car"
    | "Custom";
  customTypeName?: string;
  discoveredSources: ArboristNodeData[];
  monitoredTopics: DeviceMonitoredTopic[];
  logSyncDirectory?: string;
  logSyncEnabled?: boolean;
  site_id?: string;
  siteName?: string;
  /** Current device tags (snapshot); edit through lib/tags so history is kept. */
  tags?: Record<string, string>;
  createdAt: number;
  // Agent Version returned by backend (nullable for older/legacy agents)
  agentVersion?: string | null;

  // --- Template Linkage Fields ---
  template_id?: string | null;
  template_name?: string | null;
  template_auto_update?: boolean | null;
  template_applied_at?: string | null;
  mcapConfig?: McapRecorderConfig;
  otel_enabled?: boolean;
  /** Live monitoring switch. Absent means on (older API). Recording is separate. */
  monitoring_enabled?: boolean;
}

// --- Define State Interface ---
interface DeviceState {
  devices: Device[];
  loading: "idle" | "pending" | "succeeded" | "failed"; // More specific loading state
  error: string | null;
}

// --- Initial State ---
const initialState: DeviceState = {
  devices: [],
  loading: "idle",
  error: null,
};

// --- Async Thunks ---

// Fetch Devices
export const fetchDevices = createAsyncThunk<
  Device[], // Return type
  void, // Argument: no arguments
  { rejectValue: string } // ThunkApi config
>("devices/fetchDevices", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get<Device[]>("/devices");
    return response.data;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch devices";
    console.error("Error fetching devices:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

export const fetchDeviceDetails = createAsyncThunk<
  Device, // Return type
  { deviceId: string }, // Argument: deviceId
  { rejectValue: string } // ThunkApi config
>("devices/fetchDeviceDetails", async ({ deviceId }, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(`devices/${deviceId}/details`);

    // Use only the new monitoredTopics format
    const device: Device = {
      ...response.data,
      monitoredTopics: response.data.monitoredTopics || [],
      // Backend now returns discoveredSources already in ArboristNodeData tree form.
      discoveredSources: (response.data.discoveredSources ||
        []) as ArboristNodeData[],
    };

    return device;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch device details";
    console.error("Error fetching device details:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

export const generatePairingToken = createAsyncThunk<
  { pairingToken: string; expiresIn: number }, // Return type from backend
  void,
  { rejectValue: string }
>("devices/generatePairingToken", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.post<{
      pairing_token: string;
      expires_in: number;
    }>("devices/pairing-tokens", {});
    return {
      pairingToken: response.data.pairing_token,
      expiresIn: response.data.expires_in,
    };
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to generate pairing token";
    console.error("Error generating pairing token:", errorMsg, error.response);
    toast.error("Token Generation Failed", {
      description: errorMsg,
    });
    return rejectWithValue(errorMsg);
  }
});

// Delete Device
export const deleteDevice = createAsyncThunk<
  string,
  { deviceId: string },
  { rejectValue: string }
>("devices/deleteDevice", async ({ deviceId }, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/devices/${deviceId}`);
    return deviceId;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete device";
    console.error("Error deleting device:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

// Bulk Delete Devices
export const bulkDeleteDevices = createAsyncThunk<
  string[],
  { deviceIds: string[] },
  { rejectValue: string }
>("devices/bulkDeleteDevices", async ({ deviceIds }, { rejectWithValue }) => {
  try {
    await apiClient.delete(`/devices/bulk`, {
      data: { deviceIds },
    });
    return deviceIds;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to delete devices";
    console.error("Error bulk deleting devices:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

export interface SetDevicesMonitoringResult {
  enabled: boolean;
  updated: string[];
  /** Device id -> reason it was not switched. */
  failed: Record<string, string>;
}

// Start or stop live monitoring on one or more devices. Edge recording is
// left untouched.
export const setDevicesMonitoring = createAsyncThunk<
  SetDevicesMonitoringResult,
  { deviceIds: string[]; enabled: boolean },
  { rejectValue: string }
>(
  "devices/setDevicesMonitoring",
  async ({ deviceIds, enabled }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch<
        Omit<SetDevicesMonitoringResult, "enabled">
      >(`/devices/monitoring`, { deviceIds, enabled });
      return {
        enabled,
        updated: response.data.updated ?? [],
        failed: response.data.failed ?? {},
      };
    } catch (error) {
      const err = error as {
        message?: string;
        response?: { data?: { error?: string; message?: string } };
      };
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to update monitoring";
      console.error("Error updating device monitoring:", errorMsg);
      return rejectWithValue(errorMsg);
    }
  },
);

// Update Device (General Info - Name, Desc, Monitored Metrics)
export const updateDevice = createAsyncThunk<
  Device,
  {
    id: string;
    updates:
      | Partial<
          Pick<
            Device,
            | "name"
            | "type"
            | "customTypeName"
            | "description"
            | "mcapConfig"
            | "otel_enabled"
            | "monitoring_enabled"
          >
        >
      | { monitoredSourceIds: Record<string, string[]> };
  }, // Argument type includes token
  { state: { deviceReducer: DeviceState }; rejectValue: string }
>("devices/updateDevice", async ({ id, updates }, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch(`/devices/${id}`, updates);
    return response.data;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to update device";
    console.error("Error updating device:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

// New async thunk for getting device monitored topics
export const getDeviceMonitoredTopics = createAsyncThunk<
  DeviceMonitoredTopic[], // Return type
  { deviceId: string }, // Argument type
  { rejectValue: string } // ThunkApi config
>("devices/getMonitoredTopics", async ({ deviceId }, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(
      `devices/${deviceId}/monitored-topics`,
    );
    return response.data.topics || [];
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch monitored topics";
    console.error("Error fetching monitored topics:", errorMsg);
    return rejectWithValue(errorMsg);
  }
});

// New async thunk for setting device monitored topics
export const setDeviceMonitoredTopics = createAsyncThunk<
  DeviceMonitoredTopic[], // Return type
  { deviceId: string; topics: CreateDeviceMonitoredTopicRequest[] }, // Argument type
  { rejectValue: string } // ThunkApi config
>(
  "devices/setMonitoredTopics",
  async ({ deviceId, topics }, { rejectWithValue }) => {
    try {
      // Set device_id for all topics
      const topicsWithDeviceId = topics.map((topic) => ({
        ...topic,
        device_id: deviceId,
      }));
      await apiClient.post(`devices/${deviceId}/monitored-topics`, {
        topics: topicsWithDeviceId,
      });
      return topicsWithDeviceId as DeviceMonitoredTopic[];
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to set monitored topics";
      console.error("Error setting monitored topics:", errorMsg);
      return rejectWithValue(errorMsg);
    }
  },
);

// *** NEW: Update Device Log Sync Configuration ***
export const updateDeviceLogSyncConfig = createAsyncThunk<
  { id: string; changes: Pick<Device, "logSyncEnabled" | "logSyncDirectory"> }, // Return type for reducer
  {
    deviceId: string;
    logSyncEnabled: boolean;
    logSyncDirectory?: string;
    token: string;
  }, // Argument type
  { rejectValue: string }
>(
  "devices/updateLogSyncConfig",
  async (
    { deviceId, logSyncEnabled, logSyncDirectory },
    { rejectWithValue },
  ) => {
    try {
      const payload = {
        logSyncEnabled,
        // Only include directory if enabled, ensure it's null or string for backend
        logSyncDirectory: logSyncEnabled
          ? logSyncDirectory?.trim() || null
          : null,
      };

      // Mock response: return the intended update optimistically
      await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate delay
      return {
        id: deviceId,
        changes: {
          logSyncEnabled: payload.logSyncEnabled,
          // Return undefined if it was set to null/empty and disabled
          logSyncDirectory: payload.logSyncEnabled
            ? payload.logSyncDirectory || undefined
            : undefined,
        },
      };
    } catch (error: any) {
      console.error("Error updating log sync config:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update log sync config";
      toast.error("Update Failed", {
        description: errorMsg,
      });
      return rejectWithValue(errorMsg);
    }
  },
);

// New async thunk for detaching device template
export const detachDeviceTemplate = createAsyncThunk<
  { deviceId: string },
  { deviceId: string },
  { rejectValue: string }
>("devices/detachTemplate", async ({ deviceId }, { rejectWithValue }) => {
  try {
    await apiClient.post(`/devices/${deviceId}/template/detach`, {});
    return { deviceId };
  } catch (error: any) {
    const msg =
      error.response?.data?.error ||
      error.message ||
      "Failed to detach template";
    toast.error("Detach Failed", { description: msg });
    return rejectWithValue(msg);
  }
});

// New async thunk for toggling device template auto-update
export const toggleDeviceTemplateAutoUpdate = createAsyncThunk<
  { deviceId: string; auto: boolean; template_id?: string },
  { deviceId: string; auto: boolean; template_id?: string },
  { rejectValue: string }
>(
  "devices/toggleTemplateAutoUpdate",
  async ({ deviceId, auto, template_id }, { rejectWithValue }) => {
    try {
      // First, update the auto-update setting
      await apiClient.post(`/devices/${deviceId}/template/auto-update`, {
        auto_update: auto,
      });

      // If enabling auto-update, also sync with the current template version
      if (auto && template_id) {
        try {
          await apiClient.post(`/templates/${template_id}/apply`, {
            device_ids: [deviceId],
          });
        } catch (syncError: any) {
          // Don't fail the entire operation if sync fails, just log it
        }
      }

      return { deviceId, auto, template_id };
    } catch (error: any) {
      const msg =
        error.response?.data?.error ||
        error.message ||
        "Failed to update setting";
      toast.error("Update Failed", { description: msg });
      return rejectWithValue(msg);
    }
  },
);

// --- Slice Definition ---
const deviceSlice = createSlice({
  name: "devices",
  initialState,
  reducers: {
    updateDeviceStatusLocally: (
      state,
      action: PayloadAction<
        {
          id: string;
          online: boolean;
        }[]
      >,
    ) => {
      const statuses = action.payload;
      for (const status of statuses) {
        const index = state.devices.findIndex((d) => d.id === status.id);
        if (index !== -1) {
          state.devices[index].online = status.online;
        }
      }
    },
    // Synchronous action to update discoverable sources (e.g., from agent connection)
    updateDeviceDiscoverableSources: (
      state,
      action: PayloadAction<{ deviceId: string; sources: ArboristNodeData[] }>,
    ) => {
      const index = state.devices.findIndex(
        (d) => d.id === action.payload.deviceId,
      );
      if (index !== -1) {
        state.devices[index].discoveredSources = action.payload.sources;
        state.devices[index].online = true; // Assume online if sources are reported
        state.devices[index].lastSeenAt = new Date().getTime();
      }
    },
    // Optimistic local assignment of devices to a site
    assignDevicesToSiteLocally: (
      state,
      action: PayloadAction<{
        deviceIds: string[];
        siteId: string;
        siteName?: string;
      }>,
    ) => {
      const { deviceIds, siteId, siteName } = action.payload;
      for (const id of deviceIds) {
        const idx = state.devices.findIndex((d) => d.id === id);
        if (idx !== -1) {
          state.devices[idx].site_id = siteId;
          if (siteName) state.devices[idx].siteName = siteName;
        }
      }
    },
    unassignDevicesFromSiteLocally: (
      state,
      action: PayloadAction<{ deviceIds: string[] }>,
    ) => {
      const { deviceIds } = action.payload;
      for (const id of deviceIds) {
        const idx = state.devices.findIndex((d) => d.id === id);
        if (idx !== -1) {
          state.devices[idx].site_id = undefined;
          state.devices[idx].siteName = undefined;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Devices
      .addCase(fetchDevices.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(
        fetchDevices.fulfilled,
        (state, action: PayloadAction<Device[]>) => {
          state.loading = "succeeded";
          state.devices = action.payload;
        },
      )
      .addCase(fetchDevices.rejected, (state, action) => {
        state.loading = "failed";
        state.error = (action.payload as string) ?? "Unknown error";
      })
      .addCase(
        fetchDeviceDetails.fulfilled,
        (state, action: PayloadAction<Device>) => {
          const index = state.devices.findIndex(
            (d) => d.id === action.payload.id,
          );
          if (index !== -1) {
            // Merge updates, ensuring lastSeen is a number
            state.devices[index] = {
              ...state.devices[index],
              ...action.payload,
            };
          } else {
            state.devices.push(action.payload);
          }
        },
      )
      .addCase(generatePairingToken.rejected, (state, action) => {
        // state.pairingTokenLoading = false;
        state.error = action.payload ?? "Failed to generate token"; // Set general error or specific one
      })
      // Delete Device
      .addCase(
        deleteDevice.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.devices = state.devices.filter((d) => d.id !== action.payload);
        },
      )
      .addCase(deleteDevice.rejected, (state, action) => {
        state.error = action.payload ?? "Unknown error";
      })
      // Bulk Delete Devices
      .addCase(
        bulkDeleteDevices.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          const deletedIds = action.payload;
          state.devices = state.devices.filter(
            (d) => !deletedIds.includes(d.id),
          );
        },
      )
      .addCase(bulkDeleteDevices.rejected, (state, action) => {
        state.error = action.payload ?? "Unknown error";
      })
      .addCase(setDevicesMonitoring.fulfilled, (state, action) => {
        const updated = new Set(action.payload.updated);
        for (const device of state.devices) {
          if (updated.has(device.id))
            device.monitoring_enabled = action.payload.enabled;
        }
      })
      .addCase(setDevicesMonitoring.rejected, (state, action) => {
        state.error = action.payload ?? "Unknown error";
      })
      // Update Device (General)
      .addCase(updateDevice.fulfilled, (state, action) => {
        const index = state.devices.findIndex(
          (d) => d.id === action.payload.id,
        );
        if (index !== -1) {
          state.devices[index] = {
            ...state.devices[index],
            ...action.payload,
          };
        }
      })
      .addCase(updateDevice.rejected, (state, action) => {
        state.error = action.payload ?? "Unknown error";
      })

      // *** NEW: Update Log Sync Config Reducer ***
      .addCase(
        updateDeviceLogSyncConfig.fulfilled,
        (
          state,
          action: PayloadAction<{
            id: string;
            changes: Pick<Device, "logSyncEnabled" | "logSyncDirectory">;
          }>,
        ) => {
          const index = state.devices.findIndex(
            (d) => d.id === action.payload.id,
          );
          if (index !== -1) {
            state.devices[index].logSyncEnabled =
              action.payload.changes.logSyncEnabled;
            state.devices[index].logSyncDirectory =
              action.payload.changes.logSyncDirectory;
          }
          // Show success toast here as it confirms the update in state
          toast.success("Log Sync Updated", {
            description: `Configuration saved.`,
          });
        },
      )
      .addCase(updateDeviceLogSyncConfig.rejected, (state, action) => {
        // Error toast is handled in thunk, just update state error if needed
        state.error = action.payload as string;
      })

      // New monitored topics reducer cases
      .addCase(getDeviceMonitoredTopics.fulfilled, (state, action) => {
        // Update the device's monitored topics in state
        const deviceId = action.meta.arg.deviceId;
        const deviceIndex = state.devices.findIndex((d) => d.id === deviceId);
        if (deviceIndex !== -1) {
          state.devices[deviceIndex].monitoredTopics = action.payload;
        }
      })
      .addCase(getDeviceMonitoredTopics.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to fetch monitored topics";
      })

      .addCase(setDeviceMonitoredTopics.fulfilled, (state, action) => {
        // Update the device's monitored topics in state
        const deviceId = action.meta.arg.deviceId;
        const deviceIndex = state.devices.findIndex((d) => d.id === deviceId);
        if (deviceIndex !== -1) {
          state.devices[deviceIndex].monitoredTopics = action.payload;
        }
        toast.success("Monitored Topics Updated", {
          description: "Device monitoring configuration has been saved.",
        });
      })
      .addCase(setDeviceMonitoredTopics.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to set monitored topics";
        toast.error("Update Failed", {
          description: action.payload ?? "Failed to set monitored topics",
        });
      })
      .addCase(detachDeviceTemplate.fulfilled, (state, action) => {
        const d = state.devices.find((d) => d.id === action.payload.deviceId);
        if (d) {
          d.template_id = null;
          d.template_name = null;
          d.template_auto_update = null;
          d.template_applied_at = null;
        }
      })
      .addCase(toggleDeviceTemplateAutoUpdate.fulfilled, (state, action) => {
        const d = state.devices.find((d) => d.id === action.payload.deviceId);
        if (d) {
          d.template_auto_update = action.payload.auto;
        }
        toast.success("Auto Update Updated", {
          description: action.payload.auto
            ? "Enabled - Device synced with current template"
            : "Disabled",
        });
      });
  },
});

// --- Export Actions and Reducer ---
export const {
  updateDeviceStatusLocally,
  updateDeviceDiscoverableSources,
  assignDevicesToSiteLocally,
  unassignDevicesFromSiteLocally,
} = deviceSlice.actions;
export const deviceReducer = deviceSlice.reducer;

// --- Selectors ---
export const selectAllDevices = (state: {
  deviceReducer: DeviceState;
}): Device[] => state.deviceReducer.devices;
export const selectDeviceLoadingStatus = (state: {
  deviceReducer: DeviceState;
}): "idle" | "pending" | "succeeded" | "failed" => state.deviceReducer.loading;
export const selectDeviceError = (state: {
  deviceReducer: DeviceState;
}): string | null => state.deviceReducer.error;
export const selectDeviceById = (
  state: { deviceReducer: DeviceState },
  deviceId: string,
): Device | undefined =>
  state.deviceReducer.devices.find((device) => device.id === deviceId);
